import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSocialCoachSession, getErrorMessage, openCenteredPopup } from "@/utils";
import { CoachSocialNetworkProfileServices, CoachSocialNetworkProfile } from "@/services/bedrock";
import { SocialNetworkIcon } from "@/components";
import { socialNetworkNames, SocialNetworksId } from "@/interfaces";
import { Button, Card, Table, Spinner } from "@/components/ui";
import { CheckIcon, Trash2, Users, Settings2, Plus, Info, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { BulkAssignDialog, ActiveConnectionsDialog, DisconnectConfirmDialog } from "./components";

const SUPPORTED_COACH_NETWORKS: SocialNetworksId[] = ["FB", "IG", "LI", "GL"];

export function CoachSocialProfilesTab() {
  const { coachId, userId: sessionUserId } = useSocialCoachSession();
  const [profiles, setProfiles] = useState<CoachSocialNetworkProfile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [connectingNetwork, setConnectingConnectingNetwork] = useState<SocialNetworksId | null>(null);

  // Modal / Dialog States
  const [activeBulkAssignProfile, setActiveBulkAssignProfile] = useState<CoachSocialNetworkProfile | null>(null);
  const [activeConnectionsProfile, setActiveConnectionsProfile] = useState<CoachSocialNetworkProfile | null>(null);
  const [confirmDisconnectProfile, setConfirmDisconnectProfile] = useState<CoachSocialNetworkProfile | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | number | null>(null);

  // Fetch coach profiles
  const loadProfiles = useCallback(async () => {
    if (!coachId) return;
    setLoading(true);
    try {
      const response = await CoachSocialNetworkProfileServices.listCoachProfiles(coachId, 1, 100);
      setProfiles(response.content || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load coach social profiles", {
        description: getErrorMessage(e),
      });
    } finally {
      setLoading(false);
    }
  }, [coachId]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Handle OAuth initiate flow for a network
  const popupIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const connectParentAccount = (networkId: SocialNetworksId) => {
    if (!sessionUserId) return;
    setConnectingConnectingNetwork(networkId);

    const oauthUrl = `/api/bedrock/integrations/initiate_social_auth/${sessionUserId}/networks/${networkId}`;
    const urlWithParams = `${oauthUrl}?origin=web&is_parent=true${
      window.location.origin.includes("localhost") ? "&local=true" : ""
    }`;

    const popup = openCenteredPopup(urlWithParams, `${socialNetworkNames[networkId]}AuthPopup`, 600, 700);
    if (popupIntervalRef.current) {
      clearInterval(popupIntervalRef.current);
    }
    popupIntervalRef.current = setInterval(() => {
      if (popup?.closed) {
        if (popupIntervalRef.current) {
          clearInterval(popupIntervalRef.current);
        }
        setConnectingConnectingNetwork(null);
      }
    }, 1000);
  };

  // Listen to postMessage from the popup
  useEffect(() => {
    const handlePostMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !sessionUserId) return;

      if (event.data.type === "social_auth_success") {
        const networkId = event.data.socialNetworkId;

        toast.success(`Successfully connected parent ${socialNetworkNames[networkId as SocialNetworksId]} connection.`);
        loadProfiles();
        setConnectingConnectingNetwork(null);
      } else if (event.data.type === "social_auth_failure") {
        toast.error("Parent connection failed", {
          description: event.data.errorMessage,
        });
        setConnectingConnectingNetwork(null);
      }
    };

    window.addEventListener("message", handlePostMessage);
    return () => {
      window.removeEventListener("message", handlePostMessage);
      if (popupIntervalRef.current) {
        clearInterval(popupIntervalRef.current);
      }
    };
  }, [sessionUserId, loadProfiles]);

  const handleDisconnectParent = async (profile: CoachSocialNetworkProfile) => {
    if (!coachId) return;

    setDisconnectingId(profile.id);
    try {
      await CoachSocialNetworkProfileServices.deleteCoachProfile(coachId, profile.id);
      toast.success(`Disconnected parent ${socialNetworkNames[profile.socialNetworkId]} account.`);
      loadProfiles();
    } catch (e) {
      console.error(e);
      toast.error("Failed to disconnect parent profile", {
        description: getErrorMessage(e),
      });
    } finally {
      setDisconnectingId(null);
    }
  };

  const getExpirationStatus = (expiresAt?: string) => {
    if (!expiresAt) {
      return <span className="text-muted-foreground text-xs">—</span>;
    }

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const formattedDate = expiryDate.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    if (diffDays <= 0) {
      return (
        <div className="flex flex-col gap-0.5">
          <span className="text-destructive font-bold text-xs flex items-center gap-1">
            <AlertCircle className="size-3.5 text-destructive shrink-0" />
            Expired
          </span>
          <span className="text-[10px] text-muted-foreground">{formattedDate}</span>
        </div>
      );
    }

    if (diffDays <= 30) {
      return (
        <div className="flex flex-col gap-0.5">
          <span className="text-amber-600 font-bold text-xs flex items-center gap-1">
            <AlertCircle className="size-3.5 text-amber-500 shrink-0 animate-pulse" />
            Expires in {diffDays} day{diffDays === 1 ? "" : "s"}
          </span>
          <span className="text-[10px] text-muted-foreground">{formattedDate}</span>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-foreground text-xs">{formattedDate}</span>
        <span className="text-[10px] text-muted-foreground">{diffDays} days left</span>
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col gap-6 max-w-6xl">
      <div className="flex flex-col gap-2">
        <p className="text-muted-foreground text-sm max-w-3xl m-0">
          If you manage social media accounts on behalf of your players (such as corporate Facebook Pages or agency
          Google Business locations), connect your parent accounts here to bulk-distribute, assign, and manage child
          connections across all your players at once. You can link multiple parent accounts per network.
        </p>
      </div>

      {/* Link a New Parent Connection Action Cards */}
      <Card className="p-5 flex flex-col gap-4 bg-gray-50/50">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-bold text-foreground m-0 flex items-center gap-2">
            <Plus className="size-4 text-primary" />
            Link a New Parent Connection
          </h3>
          <p className="text-xs text-muted-foreground m-0">
            Select a network below to link a new parent account that you manage on behalf of your players.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {SUPPORTED_COACH_NETWORKS.map(networkId => (
            <Button
              key={`connect-btn-${networkId}`}
              variant="outline"
              size="sm"
              className="flex items-center justify-center gap-2 h-10 hover:bg-primary/5 hover:border-primary transition-all font-semibold"
              onClick={() => connectParentAccount(networkId)}
              disabled={connectingNetwork === networkId}
            >
              <SocialNetworkIcon socialNetworkId={networkId} size="mini" />
              {connectingNetwork === networkId ? "Connecting..." : `Link ${socialNetworkNames[networkId]}`}
            </Button>
          ))}
        </div>
      </Card>

      {/* Active Parent Connections Section */}
      <div className="flex flex-col gap-3">
        <h3 className="text-lg font-bold text-foreground m-0 flex items-center gap-2">
          <CheckIcon className="size-4 text-green-600" />
          Active Parent Connections ({profiles.length})
        </h3>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spinner className="size-8" />
          </div>
        ) : profiles.length === 0 ? (
          <Card className="p-10 flex flex-col items-center justify-center text-center gap-2 border-dashed border-gray-200">
            <Info className="size-10 text-muted-foreground opacity-50" />
            <p className="text-sm font-semibold text-muted-foreground m-0">No active parent connections found.</p>
            <p className="text-xs text-muted-foreground m-0">
              Click any button above to link your first parent account and start bulk distributing connections to
              players.
            </p>
          </Card>
        ) : (
          <div className="border border-gray-100 rounded-lg overflow-hidden bg-background">
            <Table>
              <Table.Header className="bg-gray-50/80">
                <Table.Row>
                  <Table.Head className="font-bold text-xs">Platform</Table.Head>
                  <Table.Head className="font-bold text-xs">Connected By</Table.Head>
                  <Table.Head className="font-bold text-xs">Expiration</Table.Head>
                  <Table.Head className="font-bold text-xs text-center">Assigned Players</Table.Head>
                  <Table.Head className="font-bold text-xs text-right">Actions</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {profiles.map(connectedProfile => {
                  const networkId = connectedProfile.socialNetworkId;

                  return (
                    <Table.Row key={`parent-conn-row-${connectedProfile.id}`} className="hover:bg-gray-50/20">
                      {/* Platform Column */}
                      <Table.Cell>
                        <div className="flex items-center gap-2">
                          <SocialNetworkIcon socialNetworkId={networkId} size="mini" />
                          <span className="font-semibold text-xs text-foreground">{socialNetworkNames[networkId]}</span>
                        </div>
                      </Table.Cell>

                      {/* Connected By Column */}
                      <Table.Cell>
                        {connectedProfile.connectedByUserName ? (
                          <div className="flex flex-col">
                            <span className="font-semibold text-xs text-foreground">
                              {connectedProfile.connectedByUserName}
                            </span>
                            {connectedProfile.connectedByEmail && (
                              <span className="text-[10px] text-muted-foreground">
                                {connectedProfile.connectedByEmail}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">Unknown User</span>
                        )}
                      </Table.Cell>

                      {/* Expiration Column */}
                      <Table.Cell>{getExpirationStatus(connectedProfile.expiresAt)}</Table.Cell>

                      {/* Assigned Players Column */}
                      <Table.Cell className="text-center">
                        <span
                          onClick={() => setActiveConnectionsProfile(connectedProfile)}
                          title="Click to view connected players"
                          className="inline-flex items-center gap-1 font-bold text-xs text-foreground bg-primary/5 px-2 py-0.5 rounded-full border border-primary/10 cursor-pointer hover:bg-primary/10 transition-colors"
                        >
                          <Users className="size-3 text-primary shrink-0" />
                          {connectedProfile.activeChildConnectionsCount || 0}
                        </span>
                      </Table.Cell>

                      {/* Actions Column */}
                      <Table.Cell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="default"
                            size="xs"
                            className="font-bold h-7 flex items-center gap-1"
                            onClick={() => setActiveBulkAssignProfile(connectedProfile)}
                          >
                            <Settings2 className="size-3" />
                            Bulk Assign
                          </Button>
                          <Button
                            variant="outline"
                            size="xs"
                            className="font-bold h-7 flex items-center gap-1"
                            onClick={() => setActiveConnectionsProfile(connectedProfile)}
                          >
                            <Users className="size-3" />
                            View Players
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            className="text-destructive hover:bg-destructive/10 h-7 w-7"
                            onClick={() => setConfirmDisconnectProfile(connectedProfile)}
                            disabled={disconnectingId === connectedProfile.id}
                          >
                            {disconnectingId === connectedProfile.id ? (
                              <Spinner className="size-3" />
                            ) : (
                              <Trash2 className="size-3.5" />
                            )}
                          </Button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      {/* Bulk Assign Dialog Component */}
      {activeBulkAssignProfile && (
        <BulkAssignDialog
          profile={activeBulkAssignProfile}
          onClose={() => {
            setActiveBulkAssignProfile(null);
            loadProfiles();
          }}
        />
      )}

      {/* View Active Connections Dialog Component */}
      {activeConnectionsProfile && (
        <ActiveConnectionsDialog
          profile={activeConnectionsProfile}
          onClose={() => {
            setActiveConnectionsProfile(null);
            loadProfiles();
          }}
        />
      )}

      {/* Disconnect Confirmation Dialog Component */}
      {confirmDisconnectProfile && (
        <DisconnectConfirmDialog
          profile={confirmDisconnectProfile}
          submitting={disconnectingId === confirmDisconnectProfile.id}
          onClose={() => setConfirmDisconnectProfile(null)}
          onConfirm={async () => {
            await handleDisconnectParent(confirmDisconnectProfile);
            setConfirmDisconnectProfile(null);
          }}
        />
      )}
    </div>
  );
}
